// 파일 패널(설계서 §9.4 "파일 패널") — **2단 구성**(왼쪽 내 PC · 오른쪽 원격)의 상태와 동작.
//
// 두 칸은 모양이 같지만 여는 방법이 다르다:
//  - 원격: `file` 채널의 패널 명령(`list`·`mkdir`·`delete`·`get`) — 원격이 **사용자 권한**으로 연다.
//  - 내 PC: `LocalPane`(브라우저 = 사용자가 고른 폴더 핸들 · 통합앱 = 네이티브 경로 — 뒤 단계).
//
// 전송은 전송 훅(`useFileTransfer`)에 맡긴다 — 진행 표시·취소·알림이 붙여넣기·끌어다 놓기와 **한 자리**여야 하고,
// 전송이 한 번에 한 건이라는 규칙도 한 곳에서만 지켜진다.
//
// 🔴 원격 경로는 화면에 그대로 보여 준다(사용자가 어디에 올리는지 알아야 한다). 알림·로그에는 파일 이름을 넣지 않는다.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '../sessions/types';
import { FEATURE, featureAllowed } from '../features';
import { IS_SHELL } from '../shell';
import {
  BrowserLocalPane,
  canOpenLocal,
  listLocalRoots,
  openLocalAt,
  openLocalHome,
  opensLocalOnItsOwn,
  type LocalPane,
} from './localPane';
import { FILE_ERR, FileError, joinPath, type DirItem } from './protocol';
import { nextSelection, rootOf, type SelectMode } from './panelView';
import { BrowserFileSource, type PickedFile } from './source';
import type { FileNotice, FileTransfer, TransferView } from './useFileTransfer';

export type Side = 'local' | 'remote';

/** 한 칸의 상태(두 칸이 같은 모양 → 화면은 한 벌). */
export interface PanelSide {
  /** 표시용 위치(원격은 절대 경로, 내 PC 는 고른 폴더부터의 표기). */
  label: string;
  items: DirItem[];
  loading: boolean;
  /** 사용자에게 보일 문제(목록 실패·잘림 안내). */
  error: string | null;
  /** 고른 이름들. */
  selected: string[];
  /** Shift+클릭 범위의 기준점(`panelView.nextSelection`). 목록을 새로 읽으면 선택과 함께 비운다. */
  anchor: string | null;
  canUp: boolean;
  /** 열려 있는가(내 PC 칸은 폴더를 고르기 전까지 false). */
  ready: boolean;
  /**
   * 이 기기의 드라이브(볼륨) 목록 — 칸 머리의 드라이브 선택(2026-09-29 사용자 요구). 최상위 목록(`list("")`)을 그대로 쓴다
   * (Windows `C:\`·`D:\` · macOS `/`·`/Volumes/…` · Linux `/` · 보드 `/sdcard`). 브라우저의 내 PC 칸(고른 폴더 핸들)은 없다 = 빈 목록.
   */
  roots: string[];
  /** 지금 폴더가 속한 드라이브(`roots` 중 하나). 최상위면 빈 문자열. */
  root: string;
}

export interface FilePanel {
  /** 원격이 패널을 지원하고(browse) 제어가 허용된 상태인가. */
  available: boolean;
  open: boolean;
  setOpen(v: boolean): void;
  local: PanelSide;
  remote: PanelSide;
  /** 브라우저에서 내 PC 폴더를 고를 수 있는가(Chromium 계열만). */
  canPickLocal: boolean;
  /** [폴더 열기] — **클릭 안에서** 불러야 한다(브라우저 규칙). */
  pickLocalFolder(): void;
  /**
   * 고른 파일을 원격의 연 폴더로 곧바로 보낼 수 있는가 — 브라우저만([파일 보내기…]·[폴더 보내기…]).
   * 내 PC 폴더를 열 수 없는 브라우저(Safari·Firefox)는 이것이 파일 창에서 보내는 길이다.
   * 앱은 내 PC 칸이 홈부터 열리므로 거기서 골라 [보내기 →] 한다.
   */
  canSendPicked: boolean;
  /** [파일 보내기…]·[폴더 보내기…] 결과 → 원격의 연 폴더(선택 창은 부른 쪽 창이 연다 — `pickLocalFiles`). */
  sendPicked(picked: PickedFile[], folder: boolean): void;
  /** 파일 창이 고른 내 PC 폴더(브라우저 — 선택 창은 클릭이 일어난 창에서만 열린다). */
  openLocalHandle(dir: FileSystemDirectoryHandle): void;
  /** 전송 진행·결과 알림·취소 — 파일 창이 자기 화면에도 보인다(세션 창은 도구 막대가 보인다). */
  transfer: TransferView | null;
  notice: FileNotice | null;
  cancel(): void;
  dismissNotice(): void;
  openDir(side: Side, name: string): void;
  up(side: Side): void;
  refresh(side: Side): void;
  /** 항목 고르기 — 탐색기와 같은 규칙(그냥=하나만 · Ctrl/⌘=더하기·빼기 · Shift=범위, `panelView.ts`). */
  select(side: Side, name: string, mode: SelectMode): void;
  /** 선택 해제(목록의 빈 곳 클릭). */
  clearSelection(side: Side): void;
  /** 칸 머리 드라이브 목록에서 고른 드라이브로 옮긴다(`roots` 중 하나). */
  openRoot(side: Side, root: string): void;
  mkdir(side: Side, name: string): void;
  remove(side: Side): void;
  /** 내 PC 에서 고른 것 → 원격의 연 폴더. */
  upload(): void;
  /** 원격에서 고른 것 → 내 PC 의 연 폴더. */
  download(): void;
  /** 전송이 도는 중(버튼 잠금). */
  busy: boolean;
}

/** 아직 열리지 않은 칸(파일 창도 상태를 받기 전에 이것을 그린다). */
export const EMPTY_SIDE: PanelSide = {
  label: '',
  items: [],
  loading: false,
  error: null,
  selected: [],
  anchor: null,
  canUp: false,
  ready: false,
  roots: [],
  root: '',
};
const EMPTY = EMPTY_SIDE;

/** 오류 → 사용자 문구(파일 이름은 담기지 않는다 — `FileError` 규칙). */
function textOf(e: unknown): string {
  return e instanceof FileError ? e.message : `실패했습니다: ${(e as Error)?.message ?? e}`;
}

export function useFilePanel(session: Session, files: FileTransfer): FilePanel {
  const channel = session.client.files;
  const caps = session.fileCaps;
  const available =
    !!channel && !!caps?.features.includes('browse') && session.controlAllowed !== false &&
    featureAllowed(session.features, FEATURE.FILE); // 기능 권한(§14.19.2)

  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState<PanelSide>(EMPTY);
  const [local, setLocal] = useState<PanelSide>(EMPTY);
  /** 지금 열린 원격 폴더와 위로 갈 곳 — 콜백이 낡은 값을 보지 않게 ref 로 둔다. */
  const remoteDir = useRef('');
  const remoteParent = useRef<string | null>(null);
  const pane = useRef<LocalPane | null>(null);

  // ── 원격 칸 ──────────────────────────────────────────────────────────────

  const loadRemote = useCallback(
    async (dir: string) => {
      if (!channel) {
        return;
      }
      setRemote((s) => ({ ...s, loading: true, error: null }));
      try {
        const l = await channel.list(dir);
        remoteDir.current = l.dir;
        remoteParent.current = l.parent;
        // 최상위(`dir` 빈 문자열)의 항목 = 드라이브 목록 — 한 번 받아 두면 칸 머리의 드라이브 선택에 쓴다.
        const roots = l.dir === '' ? l.items.map((it) => it.name) : null;
        setRemote((s) => ({
          label: l.dir || '이 기기',
          items: l.items,
          loading: false,
          error: l.truncated ? `항목이 많아 앞의 ${l.items.length}개만 보여 줍니다.` : null,
          selected: [],
          anchor: null,
          canUp: l.parent !== null,
          ready: true,
          roots: roots ?? s.roots,
          root: rootOf(l.dir, roots ?? s.roots),
        }));
      } catch (e) {
        // 목록을 못 읽어도 **열려 있던 위치는 유지**한다(권한 없는 폴더를 눌렀다고 패널이 처음으로 돌아가지 않게).
        setRemote((s) => ({ ...s, loading: false, error: textOf(e) }));
      }
    },
    [channel],
  );

  // 패널을 처음 열면 원격이 알려 준 시작 위치(`hello.home`)부터 — 없으면 최상위(드라이브 목록).
  useEffect(() => {
    if (open && available && !remote.ready && !remote.loading) {
      void loadRemote(caps?.home ?? '');
    }
  }, [open, available, remote.ready, remote.loading, caps?.home, loadRemote]);

  // 드라이브 목록 — 홈부터 열려 최상위를 거치지 않았으면 따로 한 번 묻는다(목록은 원격 칸 전용 명령이 아니라 같은 `list("")`).
  // 실패해도 칸은 그대로 쓴다(드라이브 선택만 안 보인다).
  const rootsAsked = useRef({ remote: false, local: false });
  useEffect(() => {
    if (!channel || !remote.ready || remote.roots.length > 0 || rootsAsked.current.remote) {
      return;
    }
    rootsAsked.current.remote = true;
    void channel.list('').then(
      (l) => {
        const roots = l.items.map((it) => it.name);
        setRemote((s) => ({ ...s, roots, root: rootOf(remoteDir.current, roots) }));
      },
      () => undefined,
    );
  }, [channel, remote.ready, remote.roots.length]);

  // ── 내 PC 칸 ─────────────────────────────────────────────────────────────

  const loadLocal = useCallback(async (next: LocalPane | null) => {
    if (!next) {
      return;
    }
    pane.current = next;
    setLocal((s) => ({ ...s, loading: true, error: null }));
    try {
      const items = await next.list();
      // 앱의 내 PC 칸만 절대 경로를 안다(`path`) — 최상위면 그 항목이 드라이브 목록이다(브라우저 칸은 드라이브 선택 없음).
      const path = next.path;
      const roots = path === '' ? items.map((it) => it.name) : null;
      setLocal((s) => ({
        label: next.label,
        items,
        loading: false,
        error: null,
        selected: [],
        anchor: null,
        canUp: next.canUp,
        ready: true,
        roots: roots ?? s.roots,
        root: path === undefined ? '' : rootOf(path, roots ?? s.roots),
      }));
    } catch (e) {
      setLocal((s) => ({ ...s, loading: false, error: textOf(e) }));
    }
  }, []);

  // 통합앱은 폴더를 고르는 단계가 없다 — 패널을 열면 내 PC 칸이 홈부터 바로 열린다(네이티브가 경로로 연다).
  useEffect(() => {
    if (open && opensLocalOnItsOwn() && !local.ready && !local.loading) {
      void openLocalHome().then(
        (p) => loadLocal(p),
        (e) => setLocal((s) => ({ ...s, error: textOf(e) })),
      );
    }
  }, [open, local.ready, local.loading, loadLocal]);

  // 앱의 내 PC 칸 드라이브 목록(원격 칸과 같은 방식 — 브라우저 칸은 없다).
  useEffect(() => {
    if (!local.ready || local.roots.length > 0 || rootsAsked.current.local || pane.current?.path === undefined) {
      return;
    }
    rootsAsked.current.local = true;
    void listLocalRoots().then(
      (roots) => setLocal((s) => ({ ...s, roots, root: rootOf(pane.current?.path ?? '', roots) })),
      () => undefined,
    );
  }, [local.ready, local.roots.length]);

  const pickLocalFolder = useCallback(() => {
    // 폴더 선택 창은 사용자 동작 안에서만 열린다 → 다른 await 보다 먼저 부른다.
    const picked = BrowserLocalPane.pick();
    void picked.then(
      (p) => loadLocal(p),
      (e) => {
        // 창을 그냥 닫은 것(AbortError)은 조용히 넘어간다.
        if ((e as DOMException)?.name !== 'AbortError') {
          setLocal((s) => ({ ...s, error: textOf(e) }));
        }
      },
    );
  }, [loadLocal]);

  const openLocalHandle = useCallback(
    (dir: FileSystemDirectoryHandle) => void loadLocal(BrowserLocalPane.fromHandle(dir)),
    [loadLocal],
  );

  // ── 두 칸 공통 동작 ──────────────────────────────────────────────────────

  const openDir = useCallback(
    (side: Side, name: string) => {
      if (side === 'remote') {
        // 최상위 목록(`dir` 이 빈 문자열)의 항목은 이름 자체가 절대 경로다 — `joinPath` 가 그 규칙을 함께 안다.
        void loadRemote(joinPath(remoteDir.current, name));
        return;
      }
      const p = pane.current;
      if (p) {
        void p.open(name).then(loadLocal, (e) => setLocal((s) => ({ ...s, error: textOf(e) })));
      }
    },
    [loadRemote, loadLocal],
  );

  const up = useCallback(
    (side: Side) => {
      if (side === 'remote') {
        if (remoteParent.current !== null) {
          void loadRemote(remoteParent.current);
        }
        return;
      }
      void pane.current?.up().then(
        (parent) => loadLocal(parent),
        (e) => setLocal((s) => ({ ...s, error: textOf(e) })),
      );
    },
    [loadRemote, loadLocal],
  );

  const refresh = useCallback(
    (side: Side) => {
      if (side === 'remote') {
        void loadRemote(remoteDir.current);
      } else {
        void loadLocal(pane.current);
      }
    },
    [loadRemote, loadLocal],
  );

  /** 한 칸의 선택만 바꾼다(두 칸 공통). */
  const updateSide = useCallback((side: Side, f: (s: PanelSide) => PanelSide) => {
    if (side === 'remote') {
      setRemote(f);
    } else {
      setLocal(f);
    }
  }, []);

  const select = useCallback(
    (side: Side, name: string, mode: SelectMode) =>
      updateSide(side, (s) => ({ ...s, ...nextSelection(s.items, s, name, mode) })),
    [updateSide],
  );

  const openRoot = useCallback(
    (side: Side, root: string) => {
      if (side === 'remote') {
        // 🔴 목록에 있는 드라이브만 — 파일 창이 보낸 임의 경로로 옮기지 않는다(경로 검사는 원격도 다시 한다).
        if (remote.roots.includes(root)) {
          void loadRemote(root);
        }
        return;
      }
      if (local.roots.includes(root)) {
        void openLocalAt(root).then(loadLocal, (e) => setLocal((s) => ({ ...s, error: textOf(e) })));
      }
    },
    [remote.roots, local.roots, loadRemote, loadLocal],
  );

  const clearSelection = useCallback(
    (side: Side) => updateSide(side, (s) => (s.selected.length === 0 ? s : { ...s, selected: [], anchor: null })),
    [updateSide],
  );

  const mkdir = useCallback(
    (side: Side, name: string) => {
      const fail = (e: unknown) =>
        side === 'remote'
          ? setRemote((s) => ({ ...s, error: textOf(e) }))
          : setLocal((s) => ({ ...s, error: textOf(e) }));
      if (side === 'remote') {
        if (!channel || !remoteDir.current) {
          return;
        }
        void channel.mkdir(remoteDir.current, name).then(() => loadRemote(remoteDir.current), fail);
        return;
      }
      const p = pane.current;
      if (p) {
        void p.mkdir(name).then(() => loadLocal(p), fail);
      }
    },
    [channel, loadRemote, loadLocal],
  );

  const remove = useCallback(
    (side: Side) => {
      const fail = (e: unknown) =>
        side === 'remote'
          ? setRemote((s) => ({ ...s, error: textOf(e) }))
          : setLocal((s) => ({ ...s, error: textOf(e) }));
      if (side === 'remote') {
        if (!channel || remote.selected.length === 0) {
          return;
        }
        void channel.remove(remoteDir.current, remote.selected).then(() => loadRemote(remoteDir.current), fail);
        return;
      }
      const p = pane.current;
      if (p && local.selected.length > 0) {
        void p.remove(local.selected).then(() => loadLocal(p), fail);
      }
    },
    [channel, remote.selected, local.selected, loadRemote, loadLocal],
  );

  // ── 올리기 · 내려받기 ────────────────────────────────────────────────────

  const upload = useCallback(() => {
    const p = pane.current;
    if (!p || !remoteDir.current || local.selected.length === 0) {
      return;
    }
    // 끝나면 원격 칸을 새로 고친다(올라간 것이 바로 보이도록).
    files.sendToDir(p.source(local.selected), remoteDir.current, () => void loadRemote(remoteDir.current));
  }, [files, local.selected, loadRemote]);

  const download = useCallback(() => {
    const p = pane.current;
    if (!channel || !p || remote.selected.length === 0) {
      return;
    }
    if (files.busy) {
      setRemote((s) => ({ ...s, error: new FileError(FILE_ERR.BUSY).message }));
      return;
    }
    void channel.get(remoteDir.current, remote.selected).then(
      (offer) => files.receiveInto(offer, p.sink(), p.label, () => void loadLocal(p)),
      (e) => setRemote((s) => ({ ...s, error: textOf(e) })),
    );
  }, [channel, files, remote.selected, loadLocal]);

  /** 고른 파일 → 원격의 연 폴더(종전 도구 막대 [📁 파일 보내기]를 파일 화면으로 옮겼다 — 2026-09-29). */
  const sendPicked = useCallback(
    (picked: PickedFile[], folder: boolean) => {
      if (picked.length === 0 || !remoteDir.current) {
        return;
      }
      files.sendToDir(
        Promise.resolve(BrowserFileSource.fromPicked(picked, folder)),
        remoteDir.current,
        () => void loadRemote(remoteDir.current),
      );
    },
    [files, loadRemote],
  );

  return {
    available,
    open,
    setOpen,
    local,
    remote,
    canPickLocal: canOpenLocal(),
    pickLocalFolder,
    canSendPicked: !IS_SHELL,
    sendPicked,
    openLocalHandle,
    transfer: files.transfer,
    notice: files.notice,
    cancel: files.cancel,
    dismissNotice: files.dismissNotice,
    openDir,
    up,
    refresh,
    select,
    clearSelection,
    openRoot,
    mkdir,
    remove,
    upload,
    download,
    busy: files.busy,
  };
}
